import { ErrorPage } from '@common/ErrorPage'
import { LoadingPage } from '@common/LoadingPage'
import { DetailView } from '@components/enitity'
import { withDetailView } from '@components/enitity/detail/Detail.view'
import { ChatWidget } from '@features/chat/views/ChatWidget'
import { OfferWidget } from '@features/offers/components/OfferWidget'
import { DELETABLE_OFFER_STATUSES } from '@features/offers/views/admin/OffersTable'
import { useDeleteOffer } from '@services/offers/mutations/useDeleteOffer'
import { useOffersDetail } from '@services/offers/queries/useOffersDetail'
import { SNA_IMAGE_PREFIX } from '@services/utils'
import {
  CalendarClock,
  Copy,
  ExternalLink,
  MapPin,
  PiggyBank,
  Trash2,
  Wallet
} from 'lucide-react'
import { type ReactNode } from 'react'
import { useTranslation } from 'react-i18next'
import { Link, useNavigate, useParams } from 'react-router-dom'

import { Card } from '@/components/ui/card'
import { useConfirm } from '@/components/ui/confirm-dialog'
import { cn } from '@/lib/utils'

const STATUS_TONE: Record<string, string> = {
  CREATED: 'bg-primary/15 text-primary',
  INTERESTED: 'bg-peach/30 text-warning',
  ACCEPTED: 'bg-primary/15 text-primary',
  RESERVED: 'bg-peach/30 text-warning',
  ONGOING: 'bg-success-soft text-success',
  NOTICE_PERIOD: 'bg-peach/30 text-warning',
  TERMINATED: 'bg-destructive/15 text-destructive',
  REJECTED: 'bg-destructive/15 text-destructive',
  CANCELED: 'bg-ink/[0.07] text-ink/60'
}

function Stat({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="min-w-0">
      <div className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
        {label}
      </div>
      <div className="mt-0.5 font-display text-base font-bold text-ink">
        {children}
      </div>
    </div>
  )
}

function OfferEditContent() {
  const { id } = useParams()
  const { t } = useTranslation('offers')
  const { data: offer, isLoading } = useOffersDetail(id)
  const navigate = useNavigate()
  const { confirm, dialog } = useConfirm()
  const deleteOffer = useDeleteOffer()

  if (isLoading) return <LoadingPage />
  if (!offer) return <ErrorPage />

  const flat = offer.flat
  const status = offer.status
  const user = offer.application?.user

  return (
    <DetailView.Root>
      <DetailView.Breadcrumb
        parentLabel={t('title')}
        parentTo="/admin/offers"
        current={flat?.title || `#${offer.id}`}
      />

      <DetailView.Content>
        {/* Hero summary */}
        <Card className="mb-6 flex flex-col gap-5 p-5 lg:flex-row lg:items-center lg:justify-between lg:gap-8 sm:p-6">
          <div className="flex min-w-0 items-center gap-4">
            <div className="h-16 w-16 shrink-0 overflow-hidden rounded-2xl bg-ink/[0.04]">
              {flat?.main_image && (
                <img
                  src={`${SNA_IMAGE_PREFIX}${flat.main_image}`}
                  alt={flat?.title ?? 'Flat'}
                  className="h-full w-full object-cover"
                  onError={(e) => {
                    e.currentTarget.style.visibility = 'hidden'
                  }}
                />
              )}
            </div>
            <div className="min-w-0">
              <div className="flex items-center gap-1.5">
                <span className="truncate font-display text-base font-bold text-ink">
                  {flat?.title || '—'}
                </span>
                {flat?.id && (
                  <Link
                    to={`/admin/flats/${flat.id}`}
                    title={t('navigation.show_flat', { ns: 'flat' })}
                    className="shrink-0 text-ink/35 transition-colors hover:text-primary"
                  >
                    <ExternalLink className="h-3.5 w-3.5" />
                  </Link>
                )}
              </div>
              {flat?.location_name && (
                <div className="mt-0.5 flex items-center gap-1.5 truncate text-sm text-muted-foreground">
                  <MapPin className="h-3.5 w-3.5 shrink-0 text-primary" />
                  {flat.location_name}
                </div>
              )}
              {user?.email && (
                <Link
                  to={`/admin/users/${user.id}`}
                  className="mt-1 inline-block truncate text-sm text-primary hover:underline"
                >
                  {user.email}
                </Link>
              )}
            </div>
          </div>

          <div className="flex flex-wrap items-center gap-x-8 gap-y-4 lg:shrink-0">
            <Stat label={t('admin.offerWidget.rent')}>
              <span className="inline-flex items-center gap-1.5">
                <Wallet className="h-4 w-4 text-primary" />
                {offer.rent ?? 0} €
              </span>
            </Stat>
            <Stat label={t('admin.offerWidget.deposit')}>
              <span className="inline-flex items-center gap-1.5">
                <PiggyBank className="h-4 w-4 text-primary" />
                {offer.deposit_sum ?? 0} €
              </span>
            </Stat>
            <Stat label={t('admin.offerWidget.deadline')}>
              <span className="inline-flex items-center gap-1.5">
                <CalendarClock className="h-4 w-4 text-primary" />
                {offer.deadline
                  ? new Date(offer.deadline).toLocaleDateString('sk-SK')
                  : '—'}
              </span>
            </Stat>
            <Stat label={t('admin.actions.offer_status')}>
              <span
                className={cn(
                  'inline-flex items-center rounded-full px-2.5 py-0.5 text-sm font-semibold',
                  STATUS_TONE[status || ''] ?? 'bg-ink/[0.07] text-ink/60'
                )}
              >
                {t(`statuses.${status}`)}
              </span>
            </Stat>
            <button
              type="button"
              onClick={() => offer.id && navigator.clipboard?.writeText(offer.id)}
              title={offer.id}
              className="inline-flex items-center gap-1.5 rounded-full border-0 bg-ink/[0.04] px-3 py-1.5 text-xs font-medium text-ink/60 transition-colors hover:bg-ink/[0.08] hover:text-ink"
            >
              <Copy className="h-3.5 w-3.5" />
              ID
            </button>
            {DELETABLE_OFFER_STATUSES.has(status ?? '') && (
              <button
                type="button"
                disabled={deleteOffer.isPending}
                onClick={async () => {
                  const confirmed = await confirm({
                    title: t('admin.actions.confirm_delete_offer'),
                    description: t('admin.actions.delete_offer_description'),
                    destructive: true,
                    confirmText: t('admin.form.yes'),
                    cancelText: t('admin.form.no')
                  })
                  if (confirmed && id) {
                    deleteOffer.mutate(
                      { id },
                      { onSuccess: () => navigate('/admin/offers') }
                    )
                  }
                }}
                className="inline-flex items-center gap-1.5 rounded-full border-0 bg-destructive/10 px-3 py-1.5 text-xs font-medium text-destructive transition-colors hover:bg-destructive hover:text-white disabled:opacity-50"
              >
                <Trash2 className="h-3.5 w-3.5" />
                {t('admin.actions.delete_offer')}
              </button>
            )}
          </div>
        </Card>
        {dialog}

        <OfferWidget offer={offer} id={id} />
        {offer.application?.id && <ChatWidget user_id={offer.application.id} />}
      </DetailView.Content>
    </DetailView.Root>
  )
}

export const OfferEdit = withDetailView(OfferEditContent, 'Offer Detail')
